import * as Yup from 'yup';
// components
import { debounce } from 'lodash';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { m, AnimatePresence } from 'framer-motion';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {
  Box,
  Fab,
  Chip,
  Alert,
  Paper,
  Stack,
  Button,
  Dialog,
  Tooltip,
  TextField,
  IconButton,
  Typography,
  DialogTitle,
  Autocomplete,
  DialogContent,
  useMediaQuery,
  CircularProgress
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import linkAnimation from 'src/assets/lottie/link.json';
import { ListEntityType } from 'src/services/lists/lists.types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { useGetListByIdQuery } from 'src/services/lists/lists.service';
import { useUploadFileMutation } from 'src/services/files/file.service';
import { getSendEmailErrorMessage } from 'src/services/email-sync/email-sync.utils';
import { closeMultiMailerDialog, restoreMultiMailerDialog, minimizeMultiMailerDialog } from 'src/store/slices/app/appSlice';
// services
import { useSendMultiEmailMutation, useGetEmailConnectionStateQuery, useGetEmailMessageTemplatesQuery } from 'src/services/email-sync/email-sync.service';
import { EmailSourceType, MailerTargetType, EmailMessageTemplate, EmailConnectionStatus, EntityTemplateVariables } from 'src/services/email-sync/email-sync.types';

import Iconify from 'src/components/iconify';
import AnimationContainer from 'src/components/animation/container';
import FormProvider, { RHFEditor, RHFTextField } from 'src/components/hook-form';

// ----------------------------------------------------------------------

export default function EmailMultiSender() {
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isClosing, setIsClosing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  const multiMailerState = useAppSelector((state) => state.app.multiMailerDialog);
  const { open, minimized, templateType, entities, targetType, listId } = multiMailerState;

  const editorRef = useRef<any>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const [focusedField, setFocusedField] = useState<'subject' | 'content' | null>(null);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [templateSearchQuery, setTemplateSearchQuery] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailMessageTemplate | null>(null);

  // File attachment states
  const [attachments, setAttachments] = useState<{ file_id: string, filename: string }[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { currentData: connectionState, isLoading: isLoadingConnection } = useGetEmailConnectionStateQuery();

  // Fetch list data if listId is provided
  const { data: listData, isLoading: isLoadingList } = useGetListByIdQuery(listId || '', {
    skip: !listId,
  });

  const {
    data: templatesData
  } = useGetEmailMessageTemplatesQuery({
    page: 1,
    per_page: 10,
    type: templateType,
    search_query: templateSearchQuery || "",
  }, {
    skip: !templateType,
  });

  const [sendMultiEmail, { isLoading: isSendingMail }] = useSendMultiEmailMutation();
  const [uploadFile] = useUploadFileMutation();

  // Calculate recipient count and label
  const recipientInfo = useMemo(() => {
    if (listId && listData) {

      let entityTypeLabel = '';

      switch (listData.entity_type) {
        case ListEntityType.CANDIDATE:
          entityTypeLabel = t('email_sync.mailer.candidates');
          break;
        case ListEntityType.CONTACT:
          entityTypeLabel = t('email_sync.mailer.contacts');
          break;
        default:
          entityTypeLabel = t('email_sync.mailer.recipients');
          break;
      }

      return {
        count: listData.entity_count,
        label: `${listData.entity_count} ${entityTypeLabel} ${t('email_sync.mailer.selected')}`,
      };
    }

    if (entities && entities.length > 0) {
      let entityTypeLabel = '';

      switch (targetType) {
        case MailerTargetType.CANDIDATE:
          entityTypeLabel = t('email_sync.mailer.candidates');
          break;
        case MailerTargetType.CONTACT:
          entityTypeLabel = t('email_sync.mailer.contacts');
          break;
        case MailerTargetType.APPLICANT:
          entityTypeLabel = t('email_sync.mailer.applicants');
          break;
        default:
          entityTypeLabel = t('email_sync.mailer.recipients');
          break;
      }

      return {
        count: entities.length,
        label: `${entities.length} ${entityTypeLabel} ${t('email_sync.mailer.selected')}`,
      };
    }

    return {
      count: 0,
      label: t('email_sync.mailer.no_recipients'),
    };
  }, [listId, listData, entities, targetType, t]);

  // Get available variables based on template type
  const availableVariables = useMemo(() => {
    if (!templateType) return {};
    return EntityTemplateVariables[templateType] || {};
  }, [templateType]);

  // schema
  const EmailFormSchema = Yup.object().shape({
    subject: Yup.string().required(t('email_sync.mailer.validation.subject_required')),
    body: Yup.string().required(t('email_sync.mailer.validation.body_required')),
  });

  // Form methods
  const methods = useForm({
    resolver: yupResolver(EmailFormSchema),
    defaultValues: {
      subject: '',
      body: '',
    },
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (open) {
      setErrorMessage(null);
      setSelectedTemplate(null);
      setAttachments([]);
      methods.reset({
        subject: '',
        body: '',
      });
    }
  }, [open, methods]);

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    setIsUploading(true);

    try {
      const files = Array.from(event.target.files);

      // eslint-disable-next-line no-restricted-syntax
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const response = await uploadFile({
          file,
          type: 'document'
        }).unwrap();

        setAttachments(prev => [
          ...prev,
          { file_id: response.id, filename: file.name }
        ]);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      enqueueSnackbar(t('common.error_uploading_file'), {
        variant: 'error',
      });
    } finally {
      setIsUploading(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove attachment
  const handleRemoveAttachment = (fileId: string) => {
    setAttachments(attachments.filter(attachment => attachment.file_id !== fileId));
  };

  const onSubmit = async (data: { subject: string; body: string }) => {
    if (recipientInfo.count === 0) {
      setErrorMessage(t('email_sync.mailer.validation.recipient_required'));
      return;
    }

    try {
      setErrorMessage(null);

      const emailSourceType = listId ? EmailSourceType.LIST : EmailSourceType.ENTITY_IDS;

      await sendMultiEmail({
        email_source_type: emailSourceType,
        entity_ids: listId ? undefined : entities,
        entity_type: targetType,
        list_id: listId || undefined,
        subject: data.subject,
        body: data.body,
        template_id: selectedTemplate?.id,
        attachments: attachments.length > 0 ? attachments.map(att => ({ file_id: att.file_id })) : undefined,
      }).unwrap();

      enqueueSnackbar(t('email_sync.api.send_multi_email.success'), {
        variant: 'success',
      });

      reset();
      setAttachments([]);
      handleClose();
    } catch (err) {
      setErrorMessage(getSendEmailErrorMessage(err));
    }
  };

  const handleClose = useCallback(() => {
    if (!isMobile) {
      setIsClosing(true);
      setTimeout(() => {
        dispatch(closeMultiMailerDialog());
        setIsClosing(false);
      }, 400);
    } else {
      dispatch(closeMultiMailerDialog());
    }
  }, [isMobile, dispatch]);

  const handleMinimize = useCallback(() => {
    dispatch(minimizeMultiMailerDialog());
  }, [dispatch]);

  const handleRestore = useCallback(() => {
    dispatch(restoreMultiMailerDialog());
  }, [dispatch]);

  const viewConnectPage = useCallback(() => {
    handleClose();
    router.push(paths.dashboard.account.email);
  }, [router, handleClose]);

  const onTemplateSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setTemplateSearchQuery(value);
    }, 500),
    [setTemplateSearchQuery]
  );

  const handleTemplateSelect = useCallback((template: EmailMessageTemplate | null) => {
    if (!template) {
      setSelectedTemplate(null);
      methods.reset({
        subject: '',
        body: '',
      });
      return;
    }

    setSelectedTemplate(template);
    methods.reset({
      subject: template.subject || '',
      body: template.content || '',
    });
  }, [methods]);

  const insertVariable = useCallback((variable: string) => {
    // Determine target field based on focus state
    const targetField = focusedField || 'content'; // Default to content if no field is focused

    if (targetField === 'subject') {
      // Insert into subject field
      const currentSubject = methods.getValues('subject');
      if (subjectRef.current) {
        const input = subjectRef.current;
        const cursorPosition = input.selectionStart || currentSubject.length;
        const newSubject =
          `${currentSubject.slice(0, cursorPosition)}${variable}${currentSubject.slice(cursorPosition)}`;
        methods.setValue('subject', newSubject, { shouldValidate: true });

        // Set cursor position after the inserted variable
        setTimeout(() => {
          if (input) {
            const newPosition = cursorPosition + variable.length;
            input.setSelectionRange(newPosition, newPosition);
            input.focus();
          }
        }, 0);
      } else {
        // Fallback: append to subject
        methods.setValue('subject', `${currentSubject}${variable}`, { shouldValidate: true });
      }
    } else if (targetField === 'content') {
      if (editorRef.current) {
        const editor = editorRef.current.getEditor();
        const range = editor.getSelection(true);
        if (range) {
          editor.insertText(range.index, variable);
          // Move cursor to the end of the inserted variable
          editor.setSelection(range.index + variable.length, 0);
          editor.focus();
        } else {
          // If no selection, append to the end
          editor.insertText(editor.getLength(), variable);
          editor.setSelection(editor.getLength(), 0);
          editor.focus();
        }
      } else {
        // Fallback: append to content
        methods.setValue('body', `${methods.getValues('body')}${variable}`, { shouldValidate: true });
      }
    }
  }, [methods, focusedField]);

  const handleSubjectFocus = useCallback(() => {
    setFocusedField('subject');
  }, []);

  const handleContentFocus = useCallback(() => {
    setFocusedField('content');
  }, []);

  // render minimized version of the dialog
  const renderMinimized = () => (
    <Fab
      color="primary"
      variant='extended'
      aria-label="restore"
      onClick={handleRestore}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 2,
        width: 'auto',
        borderRadius: '24px',
      }}
    >
      <OpenInFullIcon sx={{ fontSize: 20 }} />
      <Typography variant="button">
        {t('email_sync.mailer.restore_email')}
      </Typography>
    </Fab>
  );

  const renderContent = () => {
    if (isLoadingConnection || (listId && isLoadingList)) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (connectionState?.status !== EmailConnectionStatus.ACTIVE) {
      return (
        <Stack spacing={2} sx={{ p: 2 }} direction="column" alignItems="center">
          <AnimationContainer height={120} width={120} json={linkAnimation} loop />

          <Stack spacing={2} sx={{ p: 2 }} direction="column" alignItems="center" justifyContent="center">
            <Typography variant="body2" textAlign="center" sx={{ mb: 1 }}>
              {t('email_sync.mailer.connection_inactive')}
            </Typography>
            <Button
              variant="contained"
              endIcon={<Iconify icon="material-symbols:chat-paste-go-rounded" />}
              onClick={viewConnectPage}
            >
              {t('common.connect')}
            </Button>
          </Stack>
        </Stack>
      );
    }

    return renderEmailContent();
  }

  const renderEmailContent = () => (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        {/* Recipient Info */}
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {recipientInfo.label}
            </Typography>
          </Alert>
        </Box>

        {/* Template Selector */}
        {templateType && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('email_sync.mailer.template')}:
            </Typography>
            <Autocomplete
              fullWidth
              options={templatesData?.results || []}
              value={selectedTemplate}
              onChange={(_, newValue) => handleTemplateSelect(newValue)}
              onInputChange={(_, newInputValue) => {
                onTemplateSearchQueryChange(newInputValue);
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={t('email_sync.mailer.select_template_placeholder')}
                />
              )}
            />
          </Box>
        )}

        {/* Available Variables */}
        {Object.keys(availableVariables).length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('email_sync.templates.form.available_variables.label')}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {Object.entries(availableVariables).map(([key, value]) => (
                <Chip
                  key={key}
                  label={value as string}
                  size="small"
                  variant="outlined"
                  onClick={() => insertVariable(value as string)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {t('email_sync.templates.form.available_variables.help')}
            </Typography>
          </Box>
        )}

        {/* Subject */}
        <Box>
          <RHFTextField
            name="subject"
            label={t('email_sync.mailer.subject')}
            placeholder={t('email_sync.mailer.subject_placeholder')}
            inputRef={subjectRef}
            onFocus={handleSubjectFocus}
          />
        </Box>

        {/* Body */}
        <Box>
          <RHFEditor
            name="body"
            label={t('email_sync.mailer.body')}
            ref={editorRef}
            onFocus={handleContentFocus}
            sx={{
              '& .ql-editor': {
                minHeight: 200,
              },
            }}
          />
        </Box>

        {/* Attachments */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('email_sync.mailer.attachments')}:
          </Typography>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />

          <Button
            variant="outlined"
            startIcon={isUploading ? <CircularProgress size={20} /> : <AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isSendingMail}
            size="small"
          >
            {isUploading ? t('common.uploading') : t('email_sync.mailer.add_attachment')}
          </Button>

          {attachments.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {attachments.map((attachment) => (
                <Chip
                  key={attachment.file_id}
                  label={attachment.filename}
                  onDelete={() => handleRemoveAttachment(attachment.file_id)}
                  icon={<AttachFileIcon />}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Error Message */}
        {errorMessage && (
          <Alert severity="error">
            {errorMessage}
          </Alert>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mb: 2, mt: 1 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isSendingMail}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSendingMail || recipientInfo.count === 0}
            startIcon={isSendingMail && <CircularProgress size={20} />}
          >
            {isSendingMail ? t('email_sync.mailer.sending') : t('email_sync.mailer.send')}
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );

  if (minimized) {
    return renderMinimized();
  }

  return (
    <AnimatePresence>
      {open && !isMobile && (
        <m.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: isClosing ? 0.9 : 1, opacity: isClosing ? 0 : 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{
            duration: 0.4,
            ease: [0.43, 0.13, 0.23, 0.96]
          }}
          style={{
            position: 'fixed',
            bottom: 8,
            right: 5,
            transform: 'translate(-50%, -50%)',
            zIndex: 1300,
            maxWidth: connectionState?.status === EmailConnectionStatus.ACTIVE ? 900 : 480,
            width: '100%',
          }}
        >
          <Paper elevation={24} sx={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <DialogTitle>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">
                  {t('email_sync.mailer.send_bulk_email')}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Tooltip title={t('email_sync.mailer.minimize')}>
                    <IconButton onClick={handleMinimize} size="small">
                      <Iconify icon="mingcute:minimize-fill" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('common.close')}>
                    <IconButton onClick={handleClose} size="small">
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </DialogTitle>

            <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
              {renderContent()}
            </DialogContent>
          </Paper>
        </m.div>
      )}

      {open && isMobile && (
        <Dialog
          ref={dialogRef}
          open={open}
          onClose={handleClose}
          fullScreen
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">
                {t('email_sync.mailer.send_bulk_email')}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title={t('email_sync.mailer.minimize')}>
                  <IconButton onClick={handleMinimize} size="small">
                    <Iconify icon="mingcute:minimize-fill" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common.close')}>
                  <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </DialogTitle>

          <DialogContent>
            {renderContent()}
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
}